import React, { useEffect, useMemo, useRef, useState } from "react";
import { Archive, ChevronRight, Info, Minus, Pause, Play, Plus, Search, UserMinus } from "lucide-react";

import { apiGet, apiPatch, apiPost } from "../../Api/Client.js";
import { CopyButton } from "../../Shared/CopyButton.jsx";
import { ContributorChips, Fold, IntegrationHealthBanner, Modal, SprintProgress, TeamIdentity } from "../../Shared/ScreenComponents.jsx";
import { connectPayload, ownerCards } from "./githubOwners.js";
import "../../Styles/ProjectSanity.css";

const HEALTH_LABEL = { critical: "Past target, unresolved", warning: "Needs a look", good: "On track", dormant: "Dormant" };

// The owner cards are this company's own connected GitHub accounts (its GitHub
// connection, set by the company admin), never a fixed list: the picker is the one
// place a repo's owner gets decided, and the server refuses any other owner.

// Hover to see it's editable, click to edit in place, Enter (or blur) to
// save, Escape to back out — no separate "rename" button or modal, by
// direct instruction. A blank or unchanged name is a no-op, not a save.
function EditableSprintName({ sprint, onRename, onNotify }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(sprint.name);
  const inputRef = useRef(null);
  // Enter commits and unmounts the input; the browser may then fire blur on
  // it, which must not send the same rename a second time.
  const committedRef = useRef(false);

  useEffect(() => { setValue(sprint.name); }, [sprint.name]);
  useEffect(() => {
    if (editing) { committedRef.current = false; inputRef.current?.focus(); }
  }, [editing]);

  const commit = async () => {
    if (committedRef.current) return;
    committedRef.current = true;
    const trimmed = value.trim();
    setEditing(false);
    if (!onRename || !trimmed || trimmed === sprint.name) { setValue(sprint.name); return; }
    try {
      await onRename(sprint.id, trimmed);
    } catch (err) {
      setValue(sprint.name);
      onNotify?.(err?.payload?.detail || err?.message || "Could not rename this sprint.", false);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        className="Release-Name-Input"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") { committedRef.current = true; setValue(sprint.name); setEditing(false); }
        }}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }
  return (
    <span
      className="Release-Name Release-Name-Editable"
      title="Click to rename"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
    >
      {sprint.name}
    </span>
  );
}

// Same click-to-edit shape as the name above, for the one case the name
// editor doesn't cover: a sprint with no target date at all. "no target
// set" used to just sit there looking clickable (same cursor, no handler)
// with nothing happening on click — this is what actually makes it work.
function EditableSprintTarget({ sprint, onSetTarget, onNotify }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(sprint.target_on || "");
  const inputRef = useRef(null);

  useEffect(() => { setValue(sprint.target_on || ""); }, [sprint.target_on]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = async () => {
    setEditing(false);
    if (!onSetTarget || !value || value === sprint.target_on) { setValue(sprint.target_on || ""); return; }
    try {
      await onSetTarget(sprint.id, value);
    } catch (err) {
      setValue(sprint.target_on || "");
      onNotify?.(err?.payload?.detail || err?.message || "Could not set a target date for this sprint.", false);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="date"
        className="Release-Target-Input"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") { setValue(sprint.target_on || ""); setEditing(false); }
        }}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }
  return (
    <span
      className="Release-Target Release-Target-Editable"
      title="Click to set a target date"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
    >
      no target set
    </span>
  );
}

// "8 Oct" this year, "8 Oct 2025" otherwise. Parsed by hand from the ISO date
// the server sends: new Date("2026-10-08") reads it as UTC midnight, which
// shows as the previous day anywhere west of UTC.
function formatChipDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  if (!m) return "";
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString("en-GB", sameYear ? { day: "numeric", month: "short" } : { day: "numeric", month: "short", year: "numeric" });
}

function SprintChip({ project }) {
  const since = formatChipDate(project.status_changed_on);
  if (project.is_paused) return <span className="Sanity-Deadline">{since ? `Paused ${since}` : "Paused"}</span>;
  if (project.is_closed) return <span className="Sanity-Deadline">{since ? `Closed ${since}` : "Closed"}</span>;
  if (project.health === "dormant") return <span className="Sanity-Deadline">no activity</span>;
  const next = project.sprints?.[0];
  if (!next) return <span className="Sanity-Deadline">no sprint planned</span>;
  if (next.is_paused) return <span className="Sanity-Deadline">Paused — {next.name}</span>;
  const late = next.days_late > 0;
  return (
    <span className={"Sanity-Deadline" + (late ? " late" : "")}>
      {late ? `${next.days_late}d past target — ${next.name}` : `next: ${next.name}`}
    </span>
  );
}

// One sprint, full width. Deliberately stacked rather than sat in a two-up
// grid: a project can be running several at once and each needs room to be
// read on its own.
//
// Pause/close live here, per-card, not on the project row — hovering THIS
// card is what reveals them (2026-08-13: "if I hover on that particular
// card of the sprint, I should be able to close the sprint there, or pause
// the sprint"). Reserved as their own flex column at rest, same fix as the
// project row's own actions just got, so they can never land on top of the
// name or target text next to them.
// The weekly cycle (2026-10): propose a business goal, get it reviewed,
// have each contributor acknowledge it. Kept as its own strip under the
// existing head/progress row rather than folded into them — those two
// already carry the "what is this sprint, how far along" story; this one
// carries "is this plan actually agreed to", a separate question with its
// own separate controls.
function SprintPlanningStrip({ sprint, onPropose, onReview, onAcknowledge, onNotify, myEmployeeId }) {
  const [proposing, setProposing] = useState(false);
  const [goalDraft, setGoalDraft] = useState("");
  const [proposeBusy, setProposeBusy] = useState(false);
  const [proposeError, setProposeError] = useState("");

  const [reviewing, setReviewing] = useState(null); // "Approved" | "ChangesRequested" | null
  const [reviewFeedback, setReviewFeedback] = useState("");
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewError, setReviewError] = useState("");

  const [ackBusy, setAckBusy] = useState(false);

  const openPropose = (e) => {
    e.stopPropagation();
    setGoalDraft(sprint.business_goal || "");
    setProposeError("");
    setProposing(true);
  };
  const submitPropose = async () => {
    if (!goalDraft.trim()) return;
    setProposeBusy(true);
    setProposeError("");
    try {
      await onPropose(sprint.id, goalDraft.trim());
      setProposing(false);
    } catch (err) {
      setProposeError(err?.payload?.detail || err?.message || "Could not propose this sprint.");
    } finally {
      setProposeBusy(false);
    }
  };

  const openReview = (decision) => (e) => {
    e.stopPropagation();
    setReviewFeedback("");
    setReviewError("");
    setReviewing(decision);
  };
  const submitReview = async () => {
    if (reviewing === "ChangesRequested" && !reviewFeedback.trim()) return;
    setReviewBusy(true);
    setReviewError("");
    try {
      await onReview(sprint.id, reviewing, reviewFeedback.trim());
      setReviewing(null);
    } catch (err) {
      setReviewError(err?.payload?.detail || err?.message || "Could not save this review.");
    } finally {
      setReviewBusy(false);
    }
  };

  const acknowledge = async (e) => {
    e.stopPropagation();
    setAckBusy(true);
    try {
      await onAcknowledge(sprint.id);
    } catch (err) {
      onNotify?.(err?.payload?.detail || err?.message || "Could not acknowledge this sprint.", false);
    } finally {
      setAckBusy(false);
    }
  };

  const amPending = (sprint.pending_acknowledgement || []).some((p) => p.employee_id === myEmployeeId);
  const contributorCount = sprint.contributors?.length || 0;

  return (
    <div className="Sprint-Plan-Strip" onClick={(e) => e.stopPropagation()}>
      {sprint.is_proposed ? (
        <div className="Sprint-Plan-Proposed">
          <span className="Sprint-Plan-Badge">Awaiting review</span>
          <span className="Sprint-Plan-Goal">{sprint.business_goal}</span>
          <span className="Sprint-Plan-ReviewActions">
            <button className="ButtonSmall" onClick={openReview("Approved")}>Approve</button>
            <button className="ButtonSmall" onClick={openReview("ChangesRequested")}>Request changes</button>
          </span>
        </div>
      ) : sprint.business_goal ? (
        <div className="Sprint-Plan-Goal-Row">
          <span className="Sprint-Plan-Goal">{sprint.business_goal}</span>
        </div>
      ) : !sprint.is_done && !sprint.is_paused ? (
        <button type="button" className="Sprint-Plan-ProposeLink" onClick={openPropose}>
          + Propose a business goal for this sprint
        </button>
      ) : null}

      {sprint.latest_review && sprint.is_proposed && (
        <div className="Sprint-Plan-LastFeedback">
          Last feedback ({sprint.latest_review.by || "someone"}): {sprint.latest_review.feedback}
        </div>
      )}

      {!sprint.is_proposed && !sprint.is_done && !sprint.is_paused && contributorCount > 0 && (
        <div className="Sprint-Plan-Ack">
          <span className="Sprint-Plan-AckCount">{sprint.acknowledged_count}/{contributorCount} acknowledged</span>
          {amPending && (
            <button className="ButtonSmall" disabled={ackBusy} onClick={acknowledge}>
              {ackBusy ? "Acknowledging…" : "Acknowledge"}
            </button>
          )}
        </div>
      )}

      {proposing && (
        <Modal title="Propose a business goal" onClose={() => (proposeBusy ? null : setProposing(false))}>
          <div style={{ padding: "0 4px" }}>
            <p style={{ margin: "0 0 12px", fontSize: 13.5, color: "var(--c-600)" }}>
              What does "done" mean in plain business terms — checkable, not technical. This goes up for review before the sprint moves.
            </p>
            <textarea
              className="Mini-Inp"
              value={goalDraft}
              onChange={(e) => setGoalDraft(e.target.value)}
              placeholder="e.g. Reduce signup drop-off on the pricing page, measured by three real user recordings and the top fix shipped."
              rows={4}
              style={{ width: "100%" }}
              autoFocus
            />
            {proposeError && <div className="Auth-Alert" style={{ marginTop: 10 }}>{proposeError}</div>}
            <div className="Modal-Actions">
              <button className="Primary-Button" onClick={submitPropose} disabled={proposeBusy || !goalDraft.trim()}>
                {proposeBusy ? "Proposing…" : "Propose"}
              </button>
              <button className="Soft-Button" onClick={() => setProposing(false)} disabled={proposeBusy}>Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {reviewing && (
        <Modal title={reviewing === "Approved" ? "Approve this sprint?" : "Request changes"} onClose={() => (reviewBusy ? null : setReviewing(null))}>
          <div style={{ padding: "0 4px" }}>
            <p style={{ margin: "0 0 12px", fontSize: 13.5, color: "var(--c-600)" }}>
              <strong>{sprint.name}</strong> — {sprint.business_goal}
            </p>
            <label style={{ display: "block" }}>
              {reviewing === "Approved" ? "Feedback (optional)" : "Why — specific enough for them to fix it"}
              {reviewing === "ChangesRequested" && <span style={{ color: "var(--red-600)" }}> *</span>}
              <textarea
                className="Mini-Inp"
                value={reviewFeedback}
                onChange={(e) => setReviewFeedback(e.target.value)}
                placeholder={reviewing === "Approved" ? "Looks good, go." : "e.g. Too vague — which pages, and how will we know it worked?"}
                rows={3}
                style={{ marginTop: 6, width: "100%" }}
                autoFocus
              />
            </label>
            {reviewError && <div className="Auth-Alert" style={{ marginTop: 10 }}>{reviewError}</div>}
            <div className="Modal-Actions">
              <button
                className="Primary-Button"
                onClick={submitReview}
                disabled={reviewBusy || (reviewing === "ChangesRequested" && !reviewFeedback.trim())}
              >
                {reviewBusy ? "Saving…" : reviewing === "Approved" ? "Approve" : "Send back"}
              </button>
              <button className="Soft-Button" onClick={() => setReviewing(null)} disabled={reviewBusy}>Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function SprintBlock({ sprint, team, onPause, onResume, onClose, onRename, onSetTarget, onNotify, onPropose, onReview, onAcknowledge, myEmployeeId }) {
  const late = sprint.days_late > 0;
  // A sprint with overdue tasks inside it but not yet past its own target
  // date is still a delay in progress, not a clean close — gate the
  // acknowledgement requirement on either signal, same test the sprint
  // health panel uses to decide whether this row counts as behind at all.
  const isLate = sprint.days_late > 0 || sprint.overdue_task_count > 0;

  const [closing, setClosing] = useState(false);
  const [closeReason, setCloseReason] = useState("");
  const [closeBusy, setCloseBusy] = useState(false);
  const [closeError, setCloseError] = useState("");

  const pauseOrResume = async (e) => {
    e.stopPropagation();
    const verb = sprint.is_paused ? "Resume" : "Pause";
    if (!window.confirm(`${verb} "${sprint.name}"?`)) return;
    try {
      await (sprint.is_paused ? onResume : onPause)(sprint.id);
    } catch (err) {
      onNotify?.(err?.payload?.detail || err?.message || `Could not ${verb.toLowerCase()} this sprint.`, false);
    }
  };

  const openClose = (e) => {
    e.stopPropagation();
    setCloseReason("");
    setCloseError("");
    setClosing(true);
  };

  const confirmClose = async () => {
    if (isLate && !closeReason.trim()) return;
    setCloseBusy(true);
    setCloseError("");
    try {
      await onClose(sprint.id, isLate ? closeReason.trim() : undefined);
      setClosing(false);
    } catch (err) {
      setCloseError(err?.payload?.detail || err?.message || "Could not close this sprint.");
    } finally {
      setCloseBusy(false);
    }
  };

  return (
    <div className={"Release-Block" + (sprint.is_paused ? " paused" : "")}>
      <span className="Release-Block-Content">
        <div className="Release-Block-Head">
          <EditableSprintName sprint={sprint} onRename={onRename} onNotify={onNotify} />
          <ContributorChips contributors={sprint.contributors} team={team} />
          {sprint.is_paused ? (
            <span className="Release-Target">Paused</span>
          ) : sprint.target_on ? (
            <span className={"Release-Target" + (late ? " late" : "")}>
              {late ? `${sprint.days_late}d past ${sprint.target_on}` : `target ${sprint.target_on}`}
            </span>
          ) : (
            <EditableSprintTarget sprint={sprint} onSetTarget={onSetTarget} onNotify={onNotify} />
          )}
        </div>
        <SprintProgress sprint={sprint} />
        <SprintPlanningStrip
          sprint={sprint} onPropose={onPropose} onReview={onReview} onAcknowledge={onAcknowledge}
          onNotify={onNotify} myEmployeeId={myEmployeeId}
        />
      </span>
      <span className="Release-Block-Actions">
        <button
          type="button"
          className="Sanity-Row-Action"
          title={sprint.is_paused ? "Resume this sprint" : "Pause this sprint"}
          aria-label={sprint.is_paused ? "Resume this sprint" : "Pause this sprint"}
          onClick={pauseOrResume}
        >
          {sprint.is_paused ? <Play size={12} /> : <Pause size={12} />}
        </button>
        <button
          type="button"
          className="Sanity-Row-Action"
          title="Close this sprint"
          aria-label="Close this sprint"
          onClick={openClose}
        >
          <Archive size={12} />
        </button>
      </span>

      {closing && (
        <Modal
          title={`Close "${sprint.name}"?`}
          onClose={() => (closeBusy ? null : setClosing(false))}
        >
          <div style={{ padding: "0 4px" }}>
            <p style={{ margin: "0 0 12px", fontSize: 13.5, color: "var(--c-600)" }}>
              It moves to Closed Sprints and drops off Sprints in flight.
            </p>
            {isLate ? (
              <label style={{ display: "block" }}>
                This sprint is {sprint.days_late > 0 ? `${sprint.days_late} days past target` : "carrying overdue tasks"} — why, so it's on record before it closes <span style={{ color: "var(--red-600)" }}>*</span>
                <textarea
                  className="Mini-Inp"
                  value={closeReason}
                  onChange={(e) => setCloseReason(e.target.value)}
                  placeholder="e.g. Client delayed sign-off on the design lock — rolled remaining work into next sprint."
                  rows={3}
                  style={{ marginTop: 6, width: "100%" }}
                  autoFocus
                />
              </label>
            ) : null}
            {closeError && <div className="Auth-Alert" style={{ marginTop: 10 }}>{closeError}</div>}
            <div className="Modal-Actions">
              <button
                className="Primary-Button"
                onClick={confirmClose}
                disabled={closeBusy || (isLate && !closeReason.trim())}
              >
                {closeBusy ? "Closing…" : "Close Sprint"}
              </button>
              <button className="Soft-Button" onClick={() => setClosing(false)} disabled={closeBusy}>Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// The history list — sprints already closed on this project. Same collapse
// pattern as Team/Repositories on the Projects page (Fold), and Project
// Management only: this list is never sent to the Projects-page endpoints
// at all (see sanity_views.py), so there's nothing to hide there — it's
// simply not in the data.
function ClosedSprintBlock({ sprint }) {
  return (
    <div className="Release-Block closed">
      <span className="Release-Block-Content">
        <div className="Release-Block-Head">
          <span className="Release-Name" title={sprint.name}>{sprint.name}</span>
          <span className="Release-Target">{sprint.completed_on ? `closed ${sprint.completed_on}` : "closed"}</span>
        </div>
        <SprintProgress sprint={sprint} />
      </span>
    </div>
  );
}

// The repo-access number, with the actual repo names on hover — every one of
// them, not a truncated sample (a real project here has seventeen). Plain
// hover, not a click-to-open panel: this is reference detail someone glances
// at, not something they need to manage. It's a normal styled element rather
// than the browser's native title tooltip, which is why it can share the
// row's own font instead of falling back to the OS tooltip face.
function RepoAccessCell({ repos }) {
  const count = repos?.length || 0;
  return (
    <span className="Team-Repo-Wrap">
      <span className="Team-Repo-Count">{count}</span>
      {count > 0 && (
        <div className="Repo-Hover-Panel">
          <p className="Repo-Hover-Title">Repos still accessible here</p>
          {repos.map((name) => <div key={name} className="Repo-Hover-Item">{name}</div>)}
        </div>
      )}
    </span>
  );
}

// One row of the team list: name (blue for a full-timer — the same blue as
// their avatar), department, how many of this project's repos they still
// have access to, and a remove control. Everything after the name sits in a
// fixed-width right-hand column so it lines up across every row and with the
// add control underneath it.
function TeamRow({ member, onRemove }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const exited = member.status === "Exited";

  const remove = async () => {
    setBusy(true);
    setError("");
    try {
      await onRemove(member);
    } catch (err) {
      // The backend refuses the removal outright if GitHub access can't be
      // cleared first — the row is still here on purpose, not silently gone,
      // and this is why.
      setError(err?.payload?.detail || err?.message || "Could not remove this person.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={"Sanity-Team-Row" + (exited ? " exited" : "")}>
      <TeamIdentity member={member} />
      <RepoAccessCell repos={member.repo_access} />
      {/* A real hover panel, not the native title tooltip — the OS-level delay
         on a title tooltip is what read as sluggish; this appears the instant
         the pointer arrives, same trick as the repo-access hover above. */}
      <span className="Team-Remove-Wrap">
        <button
          type="button"
          className="Team-Remove"
          disabled={busy}
          aria-label={`Remove ${member.display_name} from this project`}
          onClick={remove}
        >
          {busy ? <span className="Team-Remove-Spinner" /> : <Minus size={13} />}
        </button>
        {!busy && (
          <div className="Repo-Hover-Panel Team-Remove-Hover">
            Remove {member.display_name} from the project. Any GitHub access will be automatically removed.
          </div>
        )}
      </span>
      {error && <div className="Team-Remove-Error">{error}</div>}
    </div>
  );
}

// Who actually has access on GitHub right now — fetched live, not read off
// Intranet's own record of who was granted access, because the two drift
// (2026-08-18/19: a mistaken unlink revealed exactly this — GitHub access
// outliving Intranet's record of it entirely). Untracked ("outside")
// entries sort first and read in red with a tag, since they're the ones
// worth a second look; a tracked teammate's row stays plain. Removing
// either kind calls the same real GitHub revoke — the tag only changes
// what gets shown, not what the remove button does.
function RemoveCollaboratorRow({ repositoryId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [collaborators, setCollaborators] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [busyUsername, setBusyUsername] = useState("");
  const [removedUsername, setRemovedUsername] = useState("");
  const [error, setError] = useState("");

  const openPanel = async () => {
    setOpen(true);
    setLoadError("");
    setError("");
    setCollaborators(null);
    try {
      const res = await apiGet(`/Project/RepositoryLinks/${repositoryId}/collaborators/`);
      setCollaborators(res.collaborators || []);
    } catch (err) {
      setLoadError(err?.payload?.detail || err?.message || "Could not load collaborators from GitHub.");
    }
  };

  const remove = async (githubUsername) => {
    setBusyUsername(githubUsername);
    setError("");
    try {
      await apiPost(`/Project/RepositoryLinks/${repositoryId}/remove-collaborator/`, { github_username: githubUsername });
      setCollaborators((current) => (current || []).filter((c) => c.github_username !== githubUsername));
      // The confirmation Bassi asked for: not just "the row is gone", a
      // sentence naming who and confirming it went through, visible for a
      // moment rather than requiring a second look to be sure.
      setRemovedUsername(githubUsername);
      window.setTimeout(() => setRemovedUsername((current) => (current === githubUsername ? "" : current)), 3000);
      onChanged?.();
    } catch (err) {
      setError(err?.payload?.detail || err?.message || "Could not remove this collaborator.");
    } finally {
      setBusyUsername("");
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        className="Icon-Action-Btn"
        title="Remove a collaborator"
        aria-label="Remove a collaborator from this repository"
        onClick={openPanel}
      >
        <UserMinus size={13} />
      </button>
    );
  }

  return (
    <div className="Remove-Collaborator-Wrap">
      <div className="Add-Member-Panel Remove-Collaborator-Panel">
        {collaborators === null && !loadError && <p className="Sanity-Empty">Loading from GitHub…</p>}
        {loadError && <div className="Team-Remove-Error">{loadError}</div>}
        {removedUsername && <p className="Action-Confirm">Removed @{removedUsername}.</p>}
        {collaborators && !collaborators.length && !loadError && (
          <p className="Sanity-Empty">Nobody currently has access to this repository on GitHub.</p>
        )}
        {collaborators && collaborators.length > 0 && (
          <div className="Add-Member-List">
            {collaborators.map((c) => (
              <div key={c.github_username} className={"Collaborator-Row" + (c.tracked ? "" : " outside")}>
                <span className="Collaborator-Identity">
                  <span className="Collaborator-Name">{c.display_name || c.github_username}</span>
                  <span className="Collaborator-Username">@{c.github_username}</span>
                  {!c.tracked && <span className="Outside-Tag">outside</span>}
                </span>
                <button
                  type="button"
                  className="Collaborator-Remove-Btn"
                  disabled={busyUsername === c.github_username}
                  onClick={() => remove(c.github_username)}
                >
                  {busyUsername === c.github_username ? "…" : "Remove"}
                </button>
              </div>
            ))}
          </div>
        )}
        {error && <div className="Team-Remove-Error">{error}</div>}
        <button type="button" className="Soft-Button Small" onClick={() => { setOpen(false); setError(""); }}>Close</button>
      </div>
    </div>
  );
}

// One linked repository, with an "Add Collaborator" control and — new
// 2026-08-13 — a way to disassociate the repo from the project entirely.
// The unlink button is a straight copy of TeamRow's own remove button one
// component up: same wrap/hover-panel/spinner/error shape, just aimed at a
// repo instead of a person. Unlike removing a team member, this action never
// refuses on a GitHub failure — Bassi's explicit instruction was no error
// message here, just an upfront hover warning naming who'll lose access, and
// the unlink itself always goes through.
function RepoRow({ repo, projectName, candidates, onAdd, onUnlink, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const unlink = async () => {
    setBusy(true);
    setError("");
    try {
      await onUnlink();
    } catch (err) {
      setError(err?.payload?.detail || err?.message || "Could not remove this repository.");
      setBusy(false);
    }
  };

  const collaborators = repo.collaborators || [];
  const hoverText = collaborators.length
    ? `Remove ${repo.name} from ${projectName}. ${collaborators.join(", ")} will lose access automatically.`
    : `Remove ${repo.name} from ${projectName}.`;

  return (
    <div className="Repo-Row">
      <span className="name">{repo.name}</span>
      <CopyButton value={repo.clone_url} label={`Copy ${repo.name}'s clone URL`} iconOnly />
      {/* Everything after the name sits in its own right-stuck group —
         explicit margin-left: auto on the group, not on whichever control
         happens to be first, since the collaborator count below only
         renders sometimes and the group still has to land in the same
         place whether it's there or not. */}
      <span className="Repo-Row-Actions">
        {/* The "did that add/remove actually take" answer — quiet at rest,
           only appears once attention has actually arrived on this row
           (2026-08-19: "only on hover"), same escalating-visibility rule as
           every other repeated-per-row control on this page. Hovering the
           count itself (once visible) shows who, reusing the exact
           Team-Repo-Wrap/Repo-Hover-Panel shape the team list's own
           repo-access hover already uses, hover-gap fix included. Omitted
           entirely at zero either way — nobody having access yet isn't
           worth a "0 collaborators" to read past. */}
        {collaborators.length > 0 && (
          <span className="Team-Repo-Wrap Repo-Collab-Count-Wrap">
            <span className="Repo-Collab-Count">{collaborators.length} {collaborators.length === 1 ? "collaborator" : "collaborators"}</span>
            <div className="Repo-Hover-Panel">
              <p className="Repo-Hover-Title">Collaborators</p>
              {collaborators.map((collabName) => <div key={collabName} className="Repo-Hover-Item">{collabName}</div>)}
            </div>
          </span>
        )}
        <AddCollaboratorRow candidates={candidates} onAdd={onAdd} />
        <RemoveCollaboratorRow repositoryId={repo.id} onChanged={onChanged} />
        <span className="Team-Remove-Wrap">
          <button
            type="button"
            className="Team-Remove"
            disabled={busy}
            aria-label={`Remove ${repo.name} from this project`}
            onClick={unlink}
          >
            {busy ? <span className="Team-Remove-Spinner" /> : <Minus size={13} />}
          </button>
          {!busy && <div className="Repo-Hover-Panel Team-Remove-Hover">{hoverText}</div>}
        </span>
      </span>
      {error && <div className="Team-Remove-Error">{error}</div>}
    </div>
  );
}

// Opens a sprint on this project — 2026-08-13, matched deliberately to look
// and feel like "Create new repository"/"Add member" (same dashed-border
// trigger, same inline form-then-submit shape). Only a name and a target
// date: tasks get attached afterward through the flows that already exist
// for that, so there's nothing else to ask for here.
function AddSprintControl({ project, onSprintAdded }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [dueOn, setDueOn] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await apiPost(`/Project/ProjectWorkspaces/${project.id}/add-sprint/`, {
        title: title.trim(), due_on: dueOn || null,
      });
      setOpen(false);
      setTitle("");
      setDueOn("");
      onSprintAdded();
    } catch (err) {
      setError(err?.payload?.detail || err?.message || "Could not add this sprint.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="Add-Repo-Btn" onClick={() => setOpen(true)}>
        + Add Sprint
      </button>
    );
  }

  return (
    <form className="Add-Task-Form" onSubmit={submit}>
      <input
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Sprint name"
        className="Add-Task-Title"
      />
      <input type="date" required value={dueOn} onChange={(e) => setDueOn(e.target.value)} title="Target date" />
      <button type="submit" className="Primary-Button Small" disabled={busy || !title.trim() || !dueOn}>
        {busy ? "Adding…" : "Add"}
      </button>
      <button type="button" className="Soft-Button Small" onClick={() => { setOpen(false); setError(""); }}>Cancel</button>
      {error && <div className="Team-Remove-Error">{error}</div>}
    </form>
  );
}

// The add-member control. A real labelled button, same shape as "Add a task"
// on the Projects page — not a bare icon — and it opens in place: the search
// panel it reveals occupies its own space below, nothing already on screen
// moves or re-measures to make room for it.
function AddMemberRow({ candidates, onAdd }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = q ? candidates.filter((e) => e.display_name.toLowerCase().includes(q)) : candidates;
    return pool.slice(0, 40);
  }, [candidates, query]);

  const pick = async (employee) => {
    setBusy(true);
    setOpen(false);
    setQuery("");
    try { await onAdd(employee); } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button type="button" className="Add-Task-Btn" disabled={busy} onClick={() => setOpen(true)}>
        <Plus size={14} /> Add member
      </button>
    );
  }

  return (
    <div className="Add-Member">
      <div className="Add-Member-Panel">
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search people…"
          className="Add-Member-Search"
        />
        <div className="Add-Member-List">
          {filtered.length
            ? filtered.map((e) => (
                <button key={e.id} type="button" className="Add-Member-Option" onClick={() => pick(e)}>
                  {e.display_name}
                </button>
              ))
            : <p className="Sanity-Empty">Nobody matches.</p>}
        </div>
        <button type="button" className="Soft-Button Small" onClick={() => { setOpen(false); setQuery(""); }}>Cancel</button>
      </div>
    </div>
  );
}

// Same trigger/panel shape as "Add member" above — the fourth control on
// this page to reuse it now. Candidates are the project's own team, minus
// whoever already shows up in this specific repo's access list — the point
// isn't "anyone in the company" (that's what scheduling a meeting is for),
// it's "who on this project still needs to be let into this one repo."
function AddCollaboratorRow({ candidates, onAdd }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [addedName, setAddedName] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = q ? candidates.filter((e) => e.display_name.toLowerCase().includes(q)) : candidates;
    return pool.slice(0, 40);
  }, [candidates, query]);

  const pick = async (employee) => {
    setBusy(true);
    setError("");
    try {
      await onAdd(employee);
      setQuery("");
      // Confirmation, not just a closed panel — the person disappearing from
      // this same candidate list (now that they have access) backs it up,
      // but that alone read as "did anything happen at all?" without a
      // sentence saying what did. Panel stays open on purpose: adding a
      // second and third person shouldn't mean reopening it each time.
      setAddedName(employee.display_name);
      window.setTimeout(() => setAddedName((current) => (current === employee.display_name ? "" : current)), 3000);
    } catch (err) {
      setError(err?.payload?.detail || err?.message || "Could not grant access.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="Add-Collaborator-Btn" disabled={busy} onClick={() => setOpen(true)}>
        Add Collaborator
      </button>
    );
  }

  return (
    <div className="Add-Member">
      <div className="Add-Member-Panel">
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the team…"
          className="Add-Member-Search"
        />
        {addedName && <p className="Action-Confirm">Added {addedName}.</p>}
        <div className="Add-Member-List">
          {filtered.length
            ? filtered.map((e) => (
                <button key={e.id} type="button" className="Add-Member-Option" disabled={busy} onClick={() => pick(e)}>
                  {e.display_name}
                </button>
              ))
            : <p className="Sanity-Empty">Everyone on the team already has access.</p>}
        </div>
        {error && <div className="Team-Remove-Error">{error}</div>}
        <button type="button" className="Soft-Button Small" onClick={() => { setOpen(false); setQuery(""); setError(""); setAddedName(""); }}>Close</button>
      </div>
    </div>
  );
}

// One panel, two modes — not two forms. The owner picker sits at the same
// position with the same markup either way; only the row beneath it swaps
// between a name field (create) and a search field (link). That's the shape
// converged on after review: the middle "create or link" choice screen is
// gone, "Create" is reached directly and is the default path, "Link
// existing" is a quiet secondary next to it rather than a competing button.
// Assigning/revoking per-person repo access and deleting a repo are each
// their own decision, deliberately left out of this control.
// Shown where the repository buttons would be, for a company that has not connected GitHub.
// The company admin connects it here once for everyone; anyone else is told who can.
function ConnectGithub({ canManage, onConnected }) {
  const [token, setToken] = useState("");
  const [owners, setOwners] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!canManage) return <p className="Sanity-Empty">Connect GitHub to add repositories. Your company admin can do this.</p>;

  const connect = async () => {
    setBusy(true);
    setError("");
    try {
      onConnected(await apiPost("/Project/ProjectWorkspaces/github-connection/", connectPayload(token, owners)));
      setToken("");
    } catch (err) {
      setError(err?.payload?.detail || err?.message || "Could not connect GitHub.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="Repo-Panel">
      <div className="Repo-Toggle-Field">
        <label>Connect GitHub</label>
        <div className="Repo-Input-Action">
          <input value={owners} onChange={(e) => setOwners(e.target.value)} placeholder="GitHub organisations, comma separated" disabled={busy} />
        </div>
        <div className="Repo-Input-Action">
          <input type="password" autoComplete="off" value={token} onChange={(e) => setToken(e.target.value)} placeholder="GitHub access token" disabled={busy} />
          <button type="button" className="Repo-Inline-Btn" onClick={connect} disabled={busy || !token.trim() || !owners.trim()}>Connect</button>
        </div>
      </div>
      {error && <div className="Team-Remove-Error">{error}</div>}
    </div>
  );
}

function AddRepositoryControl({ project, onRepoAdded }) {
  const [mode, setMode] = useState(null); // null | "create" | "link"
  const [connection, setConnection] = useState(null);
  const owners = ownerCards(connection);
  const [owner, setOwner] = useState("");
  const [name, setName] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    apiGet("/Project/ProjectWorkspaces/github-connection/")
      .then((res) => { if (alive) setConnection(res); })
      .catch(() => { if (alive) setConnection({ connected: true, owners: [] }); });
    return () => { alive = false; };
  }, []);

  const open = (nextMode) => {
    setMode(nextMode);
    setOwner(owners[0]?.value || "");
    setName("");
    setQuery("");
    setResults(null);
    setError("");
  };
  const close = () => setMode(null);

  useEffect(() => {
    if (mode !== "link") return;
    setSearching(true);
    setError("");
    const handle = setTimeout(() => {
      apiGet(`/Project/ProjectWorkspaces/${project.id}/search-repositories/?owner=${encodeURIComponent(owner)}&q=${encodeURIComponent(query)}`)
        .then((res) => setResults(res.repositories || []))
        .catch((err) => {
          // A failed search used to render as an empty list — identical to
          // "genuinely nobody matches" — which is how a dead GitHub token
          // read as "this repo doesn't exist" instead of "the search itself
          // is broken." Surface the real reason instead of guessing at one.
          setResults(null);
          setError(err?.payload?.detail || err?.message || "Could not search GitHub right now.");
        })
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, owner, query]);

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError("");
    try {
      await apiPost(`/Project/ProjectWorkspaces/${project.id}/create-repository/`, { name: name.trim(), owner, live: true });
      onRepoAdded();
      close();
    } catch (err) {
      setError(err?.payload?.detail || err?.message || "Could not create the repository.");
    } finally {
      setBusy(false);
    }
  };

  const link = async (repo) => {
    setBusy(true);
    setError("");
    try {
      await apiPost(`/Project/ProjectWorkspaces/${project.id}/create-repository/`, { name: repo.name, owner, live: true });
      onRepoAdded();
      close();
    } catch (err) {
      setError(err?.payload?.detail || err?.message || "Could not link that repository.");
    } finally {
      setBusy(false);
    }
  };

  if (connection && connection.connected === false) {
    return <ConnectGithub canManage={Boolean(connection.can_manage)} onConnected={setConnection} />;
  }

  if (!mode) {
    return (
      <div className="Add-Repo-Row">
        <button type="button" className="Add-Repo-Btn" onClick={() => open("create")}>+ Create new repository</button>
        <button type="button" className="Link-Existing-Btn" onClick={() => open("link")}>Link existing</button>
      </div>
    );
  }

  return (
    <div className="Repo-Panel">
      <button type="button" className="Repo-Panel-Back" onClick={close}>← Back to repositories</button>

      <div className="Owner-Pick">
        {owners.map((o) => (
          <button
            type="button"
            key={o.value}
            className={"Owner-Card" + (owner === o.value ? " picked" : "")}
            onClick={() => setOwner(o.value)}
          >
            <div className="Owner-Name">{o.label}</div>
            {o.use && <div className="Owner-Use">{o.use}</div>}
          </button>
        ))}
      </div>

      {mode === "create" ? (
        <>
          <div className="Repo-Toggle-Field">
            <label>Repository name</label>
            <div className="Repo-Input-Action">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="axon-mobile" disabled={busy} />
              <button type="button" className="Repo-Inline-Btn" onClick={create} disabled={busy || !name.trim()}>Create</button>
            </div>
          </div>
          <p className="Owner-Excluded-Note">✕ the personal account isn't offered here</p>
        </>
      ) : (
        <>
          <div className="Repo-Toggle-Field">
            <label>Find a repository on {owner}</label>
            <div className="Repo-Input-Action">
              <span className="Repo-Search-Icon"><Search size={13} /></span>
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…" disabled={busy} />
            </div>
          </div>
          <div className="Repo-Search-Results">
            {searching && <p className="Sanity-Empty">Searching…</p>}
            {!searching && results?.length === 0 && <p className="Sanity-Empty">Nobody matches.</p>}
            {!searching && results?.map((r) => (
              <div key={r.full_name} className="Repo-Result-Row">
                <span className="name">{r.name}</span>
                {r.already_linked
                  ? <span className="already">already linked</span>
                  : <button type="button" className="link-btn" disabled={busy} onClick={() => link(r)}>Link</button>}
              </div>
            ))}
          </div>
        </>
      )}
      {error && <div className="Team-Remove-Error">{error}</div>}
    </div>
  );
}

function SanityRow({ project, open, onToggle, employees, onTeamChanged, onRepoAdded, onPause, onResume, onCloseProject, onPauseSprint, onResumeSprint, onCloseSprint, onRenameSprint, onSetSprintTarget, onNotify, onProposeSprint, onReviewSprint, onAcknowledgeSprint, myEmployeeId }) {
  const remove = (member) => onTeamChanged(project.id, { type: "remove", member });
  const add = (employee) => onTeamChanged(project.id, { type: "add", employee });

  const onTeamIds = useMemo(() => new Set(project.team.map((t) => t.employee_id)), [project.team]);
  const candidates = useMemo(
    () => (employees || []).filter((e) => !onTeamIds.has(e.id) && e.status !== "Exited"),
    [employees, onTeamIds],
  );

  const grantRepoAccess = async (repo, employee) => {
    await apiPost(`/Project/RepositoryLinks/${repo.id}/access/`, {
      employee: employee.id, access_status: "Granted", live: true,
    });
    onRepoAdded();
  };

  const unlinkRepo = async (repo) => {
    await apiPost(`/Project/RepositoryLinks/${repo.id}/unlink/`, {});
    onRepoAdded();
  };

  const pauseOrResume = async (e) => {
    e.stopPropagation();
    const verb = project.is_paused ? "Resume" : "Pause";
    if (!window.confirm(`${verb} "${project.name}"?${project.is_paused ? "" : " It'll sink to the bottom of the list and grey out until resumed."}`)) return;
    try {
      await (project.is_paused ? onResume : onPause)(project.id);
    } catch (err) {
      onNotify?.(err?.payload?.detail || err?.message || `Could not ${verb.toLowerCase()} this project.`, false);
    }
  };
  const closeProject = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Close "${project.name}"? This marks it done — it won't reopen from here.`)) return;
    try {
      await onCloseProject(project.id);
    } catch (err) {
      onNotify?.(err?.payload?.detail || err?.message || "Could not close this project.", false);
    }
  };

  return (
    <div id={`pm-project-${project.id}`} className={"Sanity-Row" + (open ? " open" : "") + (project.is_paused || project.is_closed ? " paused" : "")}>
      <div className="Sanity-Row-Head" onClick={onToggle}>
        {/* Grayed as its own wrapper, not the whole strip — the pause/close
           controls below stay full-color even on an already-paused row, or
           the way back out would be just as faded as everything else. This
           wrapper carries the row's original layout untouched — health dot,
           name, and everything after it sit exactly where they always did,
           so the expanded body underneath still lines up under the name the
           way it's supposed to. */}
        <span className="Sanity-Row-Content">
          <span className={`Health-Dot ${project.health}`} title={HEALTH_LABEL[project.health]} />
          <span className="Sanity-Name">{project.name}</span>
          <span className="Sanity-Team-Count">{project.team_count} on team</span>
          {project.sprint_count > 1 && <span className="Sanity-Release-Count">{project.sprint_count} sprints</span>}
          <SprintChip project={project} />
        </span>
        {/* A reserved column in the row's own flex flow, sitting between the
           content and the chevron — its width never changes, only its
           visibility does on hover, so it can never land on top of the
           content next to it. */}
        <span className="Sanity-Row-Actions">
          <button
            type="button"
            className="Sanity-Row-Action"
            title={project.is_paused ? "Resume this project" : "Pause this project"}
            aria-label={project.is_paused ? "Resume this project" : "Pause this project"}
            onClick={pauseOrResume}
          >
            {project.is_paused ? <Play size={13} /> : <Pause size={13} />}
          </button>
          <button
            type="button"
            className="Sanity-Row-Action"
            title="Close this project (marks it done)"
            aria-label="Close this project"
            onClick={closeProject}
          >
            <Archive size={13} />
          </button>
        </span>
        <ChevronRight size={15} className="Sanity-Chev" />
      </div>
      {open && (
        <div className="Sanity-Row-Body">
          {/* No reason banner here at all, by explicit instruction (2026-08-05:
             "I don't want that card at all... whatever that card is called
             has exited or anything that's there in that card"). The health
             dot plus what's visibly red on the sprint cards below is the
             whole signal now. `project.reason`/`reason_kind` are still
             computed and returned — nothing backend-side depends on this
             banner existing, so removing it here is the entire change. */}

          <div className="Sanity-Block-Label-Row">
            <p className="Sanity-Block-Label">Sprints in flight</p>
            <AddSprintControl project={project} onSprintAdded={onRepoAdded} />
          </div>
          {project.sprints?.length
            ? project.sprints.map((s) => (
                <SprintBlock
                  key={s.id} sprint={s} team={project.team}
                  onPause={onPauseSprint} onResume={onResumeSprint} onClose={onCloseSprint} onRename={onRenameSprint}
                  onSetTarget={onSetSprintTarget} onNotify={onNotify}
                  onPropose={onProposeSprint} onReview={onReviewSprint} onAcknowledge={onAcknowledgeSprint}
                  myEmployeeId={myEmployeeId}
                />
              ))
            : <p className="Sanity-Empty">No open sprints.</p>}

          {project.closed_sprint_count > 0 && (
            <Fold label="Closed sprints" count={project.closed_sprint_count}>
              {project.closed_sprints.map((s) => <ClosedSprintBlock key={s.id} sprint={s} />)}
            </Fold>
          )}

          {project.delays?.length > 0 && (
            <>
              <p className="Sanity-Block-Label">Open delays</p>
              {project.delays.map((d) => (
                <p key={d.id} className="Sanity-Delay">
                  <span className="Sanity-Delay-Days">{d.days}d</span>
                  {d.reason || "No reason given."}
                </p>
              ))}
            </>
          )}

          <div className="Sanity-Grid">
            <div>
              <p className="Sanity-Block-Label">Team</p>
              {project.team.length
                ? project.team.map((t) => <TeamRow key={t.employee_id} member={t} onRemove={remove} />)
                : <p className="Sanity-Empty">No one assigned.</p>}
              <AddMemberRow candidates={candidates} onAdd={add} />
            </div>
            <div>
              <p className="Sanity-Block-Label">Repositories</p>
              {project.repos?.length
                ? project.repos.map((r) => {
                    const repoCandidates = project.team
                      .filter((t) => !(t.repo_access || []).includes(r.name))
                      .map((t) => ({ id: t.employee_id, display_name: t.display_name }));
                    return (
                      <RepoRow
                        key={r.id}
                        repo={r}
                        projectName={project.name}
                        candidates={repoCandidates}
                        onAdd={(employee) => grantRepoAccess(r, employee)}
                        onUnlink={() => unlinkRepo(r)}
                        onChanged={onRepoAdded}
                      />
                    );
                  })
                : <p className="Sanity-Empty">None linked.</p>}
              <AddRepositoryControl project={project} onRepoAdded={onRepoAdded} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// The page's explanation and colour key, tucked behind an info icon next to
// the title -- it's reference, read once, not something to look at every
// visit. Hover (or keyboard focus) opens it; same invisible hover-bridge idea
// as the other panels on this page, so moving the pointer down into it never
// drops the hover.
function PageInfo() {
  return (
    <span className="Sanity-Info">
      <button type="button" className="Sanity-Info-Btn" aria-label="About this page">
        <Info size={16} />
      </button>
      <div className="Sanity-Info-Panel" role="tooltip">
        <p className="Sanity-Info-Text">
          Every sprint in flight, across every project. Health is computed fresh from what's actually happening — nothing here is hand-typed.
        </p>
        <div className="Sanity-Info-Legend">
          <span><span className="Health-Dot critical" /> Past target, unresolved</span>
          <span><span className="Health-Dot warning" /> Needs a look</span>
          <span><span className="Health-Dot good" /> On track</span>
          <span><span className="Health-Dot dormant" /> Dormant — no activity</span>
        </div>
      </div>
    </span>
  );
}

// Name and Enter, nothing else: the button turns into the input in place.
// Everything beyond the name (sprints, people, repositories) is added from
// the expanded row afterwards. Enter creates, Escape backs out, and leaving
// the field empty closes it again rather than leaving a dead input behind.
function QuickAddProject({ onCreate }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  const close = () => { setOpen(false); setName(""); };

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      await onCreate(trimmed);
      close();
    } catch {
      // The screen has already put the reason on the page banner; the name
      // stays in the field so it can be fixed and sent again.
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="Sanity-Add-Btn" onClick={() => setOpen(true)}>
        <Plus size={14} /> Add project
      </button>
    );
  }

  return (
    <input
      ref={inputRef}
      className="Sanity-Add-Input"
      value={name}
      disabled={busy}
      maxLength={220}
      placeholder="Project name, then Enter"
      aria-label="New project name"
      onChange={(e) => setName(e.target.value)}
      onBlur={() => { if (!name.trim() && !busy) close(); }}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); submit(); }
        if (e.key === "Escape") close();
      }}
    />
  );
}

// Remembers a project the viewer just created, so it is still the one open
// after a refresh -- cleared the moment they open or close anything by hand.
// Per-viewer convenience only; every access is guarded since storage can be
// unavailable (private window, blocked site data).
const JUST_CREATED_KEY = "pm.openAfterCreate";
const readJustCreated = () => { try { return Number(window.localStorage.getItem(JUST_CREATED_KEY)) || null; } catch { return null; } };
const writeJustCreated = (id) => {
  try {
    if (id) window.localStorage.setItem(JUST_CREATED_KEY, String(id));
    else window.localStorage.removeItem(JUST_CREATED_KEY);
  } catch { /* nothing to do -- it just won't survive a refresh */ }
};

export function ProjectManagementScreen({ data }) {
  const [projects, setProjects] = useState(null);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);
  const [showClosed, setShowClosed] = useState(false);
  const restoredOpen = useRef(false);
  const employees = data?.employees;

  // Who's actually looking at this page — needed to know whether "Acknowledge"
  // applies to them on a given sprint, same resolution Delay Management
  // already uses (data.me -> the matching EmployeeProfile, not just the user).
  const me = data?.me?.user || data?.me?.account || data?.me || {};
  const myEmployeeId = useMemo(
    () => (data?.employees || []).find((e) => String(e.user) === String(me.id))?.id ?? null,
    [data?.employees, me.id],
  );

  // Every pause/resume/close/rename action below used to fail silently — no
  // catch, no feedback, so a rejected request just looked like nothing
  // happened. One shared banner for all of them, same {ok, message} shape
  // Delay Management already uses.
  const [feedback, setFeedback] = useState({ ok: false, message: "" });
  // One timer: a newer message restarts the 5 seconds instead of being
  // cleared early by the older message's timer.
  const feedbackTimer = useRef(null);
  useEffect(() => () => clearTimeout(feedbackTimer.current), []);
  const notify = (message, ok = false) => {
    setFeedback({ ok, message });
    clearTimeout(feedbackTimer.current);
    feedbackTimer.current = setTimeout(() => setFeedback({ ok: false, message: "" }), 5000);
  };

  const load = () =>
    apiGet("/Project/sanity/")
      .then((res) => {
        const rows = res.projects || [];
        setProjects(rows);
        // First load only: bring back the project this viewer just created.
        if (!restoredOpen.current) {
          restoredOpen.current = true;
          const remembered = readJustCreated();
          if (remembered && rows.some((p) => p.id === remembered)) setOpenId(remembered);
        }
      })
      .catch((e) => setError(e?.payload?.detail || e?.message || "Could not load Project Management."));

  useEffect(() => { load(); }, []);

  // Closed projects are history, not "in flight" -- hidden unless asked for.
  // A paused one stays visible (greyed): it's expected back.
  const closedCount = useMemo(() => (projects || []).filter((p) => p.is_closed).length, [projects]);
  const visibleProjects = useMemo(
    () => (projects || []).filter((p) => showClosed || !p.is_closed),
    [projects, showClosed],
  );

  const toggleRow = (id) => {
    writeJustCreated(null);
    setOpenId((current) => (current === id ? null : id));
  };

  // The creator is put on the team server-side. Opening it straight away and
  // scrolling it into view matters because a brand-new, empty project sorts
  // among the quiet ones and could otherwise land off-screen.
  const createProject = async (name) => {
    try {
      const res = await apiPost("/Project/sanity/create/", { name });
      writeJustCreated(res.id);
      setOpenId(res.id);
      await load();
      window.requestAnimationFrame(() => {
        document.getElementById(`pm-project-${res.id}`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      });
      notify(`Created "${res.name}" — you're on its team.`, true);
    } catch (err) {
      notify(err?.payload?.detail || err?.message || "Could not create this project.", false);
      throw err;
    }
  };

  // Adding still feels instant — the row appears before the network call
  // resolves, then a silent reload fills in the department and repo count
  // only the server knows. Removing does NOT get the same treatment: a
  // removal can genuinely be refused (GitHub access has to clear first), so
  // the person stays in the list — with a loader on their own row — until
  // the server actually confirms it. `openId` isn't touched by either path,
  // so the panel someone has open never jumps shut underneath them.
  const onTeamChanged = async (projectId, action) => {
    if (action.type === "add") {
      setProjects((current) =>
        current.map((p) => p.id !== projectId ? p : {
          ...p,
          team: [...p.team, {
            employee_id: action.employee.id,
            display_name: action.employee.display_name,
            initials: null,
            department: null,
            is_full_time: false,
            status: "Active",
            repo_access: [],
          }],
          team_count: p.team_count + 1,
        }),
      );
      try {
        await apiPost(`/Project/ProjectWorkspaces/${projectId}/add-member/`, {
          employee: action.employee.id, role: "Member",
        });
      } catch (err) {
        // The reload below takes the row we showed early back off the team; say why.
        notify(err?.payload?.detail || err?.message || "Could not add this person to the team.", false);
      } finally {
        load();
      }
      return;
    }

    // "remove" — let a failure propagate to the row's own catch block instead
    // of swallowing it here, so the button can show why it didn't work.
    await apiPost(`/Project/TeamAssignments/${action.member.assignment_id}/remove/`, {});
    load();
  };

  const pauseProject = async (projectId) => {
    await apiPost(`/Project/board/${projectId}/pause/`, {});
    load();
  };
  const resumeProject = async (projectId) => {
    await apiPost(`/Project/board/${projectId}/resume/`, {});
    load();
  };
  const closeProject = async (projectId) => {
    await apiPost(`/Project/board/${projectId}/close/`, {});
    load();
  };

  const pauseSprint = async (sprintId) => {
    await apiPost(`/Project/DeliveryMilestones/${sprintId}/pause/`, {});
    load();
  };
  const resumeSprint = async (sprintId) => {
    await apiPost(`/Project/DeliveryMilestones/${sprintId}/resume/`, {});
    load();
  };
  const closeSprint = async (sprintId, reason) => {
    // A late sprint logs why it was late on the way out, in the same call: the server closes
    // it and writes the same record Delay Management's own "Acknowledge" button writes, both
    // or neither, so the reason shows up there ("recently closed, was late") instead of the
    // sprint vanishing off the active list unexplained, and a failure never leaves it half done.
    await apiPost(`/Project/DeliveryMilestones/${sprintId}/complete/`, reason ? { reason } : {});
    load();
  };
  const renameSprint = async (sprintId, title) => {
    await apiPatch(`/Project/DeliveryMilestones/${sprintId}/`, { title });
    load();
  };
  const setSprintTarget = async (sprintId, dueOn) => {
    await apiPatch(`/Project/DeliveryMilestones/${sprintId}/`, { due_on: dueOn });
    load();
  };

  const proposeSprint = async (sprintId, businessGoal) => {
    await apiPost(`/Project/DeliveryMilestones/${sprintId}/propose/`, { business_goal: businessGoal });
    load();
  };
  const reviewSprint = async (sprintId, decision, feedback) => {
    await apiPost(`/Project/DeliveryMilestones/${sprintId}/review/`, { decision, feedback });
    load();
  };
  const acknowledgeSprint = async (sprintId, note) => {
    await apiPost(`/Project/DeliveryMilestones/${sprintId}/acknowledge/`, { note });
    load();
  };

  return (
    <section className="Sanity-Page">
      <IntegrationHealthBanner />
      <div className="Sanity-Head">
        <div className="Sanity-Title">
          <h1>Project Management</h1>
          <PageInfo />
        </div>
        <div className="Sanity-Head-Actions">
          {closedCount > 0 && (
            <button
              type="button"
              className={"Sanity-Closed-Toggle" + (showClosed ? " on" : "")}
              aria-pressed={showClosed}
              onClick={() => setShowClosed((v) => !v)}
            >
              Show closed ({closedCount})
            </button>
          )}
          <QuickAddProject onCreate={createProject} />
        </div>
      </div>

      {feedback.message && (
        <div className={feedback.ok ? "Auth-AlertOk" : "Auth-Alert"} style={{ marginBottom: "1rem" }}>
          {feedback.message}
        </div>
      )}

      {error && <div className="Sanity-Why critical">{error}</div>}
      {!error && projects === null && <p className="Sanity-Empty">Loading…</p>}
      {!error && projects && !projects.length && <p className="Sanity-Empty">No projects yet.</p>}
      {!error && projects && projects.length > 0 && !visibleProjects.length && (
        <p className="Sanity-Empty">Every project is closed.</p>
      )}

      {!error && visibleProjects.length > 0 && (
        <div className="Sanity-List">
          {visibleProjects.map((p) => (
            <SanityRow
              key={p.id}
              project={p}
              open={openId === p.id}
              onToggle={() => toggleRow(p.id)}
              employees={employees}
              onTeamChanged={onTeamChanged}
              onRepoAdded={load}
              onPause={pauseProject}
              onResume={resumeProject}
              onCloseProject={closeProject}
              onPauseSprint={pauseSprint}
              onResumeSprint={resumeSprint}
              onCloseSprint={closeSprint}
              onRenameSprint={renameSprint}
              onSetSprintTarget={setSprintTarget}
              onNotify={notify}
              onProposeSprint={proposeSprint}
              onReviewSprint={reviewSprint}
              onAcknowledgeSprint={acknowledgeSprint}
              myEmployeeId={myEmployeeId}
            />
          ))}
        </div>
      )}
    </section>
  );
}
